import { useEffect, useMemo } from 'react'
import { isMobile } from 'react-device-detect'
import TagManager, { DataLayerArgs } from 'react-gtm-module'
import styled from 'styled-components'
import { useAppContext } from '../../components/provider/AppProvider'
import { useCommonContext } from '../../components/provider/CommonProvider'
import { PageTitle } from '../../components/title/PageTitle'
import { handlePostMessage, sendSetAppsflyerEvent } from '../../js/postMessage'
import {
  SET_INVALID_ORDER_VARIATION_MAP,
  SET_ORDER_PRODUCT_MAP,
  SET_ORDER_VARIATION_MAP,
  SET_SELECT_ORDER_VARIATION_MAP,
  SET_TOTAL_CART_PRICE_INFO,
} from './CartGroupProvider'
import useCartGroup from './useCartGroup'
import EmptyCartFragment from './EmptyCartFragment'
import CartPriceInfo from './CartPriceInfo'
import CartGroupHeader from './CartGroupHeader'
import CartProductItem from './CartProductItem'

import '../../css/Cart.css'

export default function CartList() {
  const { isAppView } = useAppContext()
  const { hasAccessToken } = useCommonContext()

  const {
    isInvalid,
    orderVariationMap,
    invalidOrderVariationMap,
    selectVariationCartNos,
    totalCartPriceInfo,
    dispatch,
    fetchCartData,
    generateOrderCartMap,
    fetchCategories,
    fetchUserCredit,
    fetchFirstPurchase,
    createGTMItems,
  } = useCartGroup()

  const combineOrderVariationMap = useMemo(() => {
    return {
      ...orderVariationMap,
      ...invalidOrderVariationMap,
    }
  }, [orderVariationMap, invalidOrderVariationMap])

  const orderVariationMapList = useMemo(() => {
    if (isInvalid) return Object.values(invalidOrderVariationMap)
    return Object.values(orderVariationMap)
  }, [isInvalid, invalidOrderVariationMap, orderVariationMap])

  // 첫구매 여부 조회
  useEffect(() => {
    if (!hasAccessToken) return
    fetchFirstPurchase()
  }, [hasAccessToken])

  // 최대 할인 쿠폰, 피싱코인, 적립급 조회
  useEffect(() => {
    if (hasAccessToken && selectVariationCartNos.length !== 0) {
      fetchUserCredit()
    }
  }, [hasAccessToken, selectVariationCartNos])

  // 카테고리 조회
  useEffect(() => {
    fetchCategories()
  }, [])

  // 장바구니 조회
  useEffect(() => {
    const onFetchCartData = async () => {
      try {
        const fetchCartReponse = await fetchCartData()

        if (fetchCartReponse) {
          const { price } = fetchCartReponse
          const cartMapReponse = generateOrderCartMap(fetchCartReponse)
          const { orderProductMap, orderVariationMap, invalidOrderVariationMap, selectOrderVariationMap } =
            cartMapReponse

          dispatch({
            type: SET_ORDER_PRODUCT_MAP,
            payload: orderProductMap,
          })

          dispatch({
            type: SET_ORDER_VARIATION_MAP,
            payload: orderVariationMap,
          })
          dispatch({
            type: SET_INVALID_ORDER_VARIATION_MAP,
            payload: invalidOrderVariationMap,
          })

          dispatch({
            type: SET_SELECT_ORDER_VARIATION_MAP,
            payload: selectOrderVariationMap,
          })

          dispatch({
            type: SET_TOTAL_CART_PRICE_INFO,
            payload: price,
          })

          handlePostMessage({
            actionType: 'goCart',
            params: { mobile: isMobile ? 'mobile' : 'pc', productId: Object.keys(orderProductMap) },
          })
        }
      } catch (error) {
        console.error('장바구니 조회 중 오류 발생:', error)
      }
    }

    onFetchCartData()
  }, [])

  // gtmItem 만들기, 이벤트 전달
  useEffect(() => {
    const orderVariationMapList = Object.values(combineOrderVariationMap)

    if (orderVariationMapList.length === 0) return

    const sendViewCartEvent = async () => {
      try {
        const gtmItems = await createGTMItems(orderVariationMapList)

        if (gtmItems && gtmItems.length === orderVariationMapList.length && totalCartPriceInfo) {
          const gtmCartItem: DataLayerArgs = {
            dataLayer: {
              event: 'view_cart', // ecommerce 이벤트 중 상품보기 단계
              ecommerce: {
                currency: 'KRW',
                value: totalCartPriceInfo.buyAmt,
                items: gtmItems,
              },
            },
          }

          if (isAppView) {
            sendSetAppsflyerEvent({
              eventName: 'view_cart',
              eventType: {
                currency: 'KRW',
                value: totalCartPriceInfo.buyAmt,
                items: gtmItems,
              },
            })
          } else {
            TagManager.dataLayer(gtmCartItem)
          }
        }
      } catch (error) {
        console.error('Error Send View Cart Event:', error)
      }
    }

    sendViewCartEvent()
  }, [combineOrderVariationMap, totalCartPriceInfo])

  useEffect(() => {
    const script = document.createElement('script')
    script.src = 'https://shop-api.e-ncp.com/payments/ncp_pay.js'
    script.async = true
    document.body.appendChild(script)

    const script3 = document.createElement('script')
    script3.src = 'https://nsp.pay.naver.com/sdk/js/naverpay.min.js'
    script3.async = true
    document.body.appendChild(script3)
  }, [])

  //  장바구니 없을 때 박스 높이 조절
  useEffect(() => {
    const contentsWrap = document.querySelector('.contents_wrap')
    if (!contentsWrap) return

    contentsWrap.classList.toggle('full', Object.values(combineOrderVariationMap).length === 0)
  }, [combineOrderVariationMap])

  useEffect(() => {
    const footer = document.querySelector('.footer_wrap') as HTMLElement
    if (!footer) return
    const paddingBottom = isMobile ? (combineOrderVariationMap.length !== 0 ? '108px' : '36px') : '0'
    footer.style.paddingBottom = paddingBottom
  }, [combineOrderVariationMap])

  return (
    <>
      <div className='main_wrap' style={{ minHeight: 'calc(100vh - 300px)' }}>
        <div className='contents_wrap'>
          <PageTitle>장바구니</PageTitle>
          <CartListWrap>
            <div className='cart_list_wrap'>
              <div className='left_area'>
                {/* 장바구니 헤더 */}
                <CartGroupHeader />
                {/* 장바구니 목록 */}
                {orderVariationMapList.length > 0 ? (
                  <CartListItemWrap>
                    {orderVariationMapList.map((variation: any) => {
                      return <CartProductItem key={variation.optionNo} variation={variation} />
                    })}
                  </CartListItemWrap>
                ) : (
                  <EmptyCartFragment />
                )}
              </div>
              {/* 장바구니 결제버튼 영역 */}
              <CartPriceInfo />
            </div>
          </CartListWrap>
        </div>
      </div>
    </>
  )
}

// 장바구니 전체 wrap
export const CartListWrap = styled.div`
  width: 100%;
  & .cart_header {
    display: flex;
    font-size: 14px;
    font-weight: 600;
    line-height: 24px;
    align-items: center;
    justify-content: space-between;
    padding: 8px 20px;
    & .cart_item_num {
      color: var(--colGray6);
      font-size: 16px;
      line-height: 24px;
      font-weight: 500;
      & span {
        color: var(--colBlack);
      }
    }
    @media screen and (min-width: 768px) {
      font-size: 16px;
      padding: 12px 0;
      border-bottom: 1px solid var(--colGray3);
      & .cart_item_num {
        color: var(--colGray6);
        font-weight: 500;
      }
      & button {
        font-size: 14px;
        font-weight: 500;
        line-height: 24px;
        & img {
          width: 20px;
          height: 20px;
        }
      }
      & span {
        color: var(--colBlack);
        font-weight: 600;
      }
    }
  }
  & hr {
    height: 8px;
    background: var(--colGray1);
    border: none;
    margin: 0;
  }
`
// 장바구니 목록 wrap
export const CartListItemWrap = styled.div`
  padding: 24px 20px 0 20px;
  & .cart_item {
    margin-bottom: 24px;
  }
  @media screen and (min-width: 768px) {
    padding: 32px 0;
    & .cart_item {
      margin-bottom: 32px;
    }
  }
`

// 장바구니 옵션 수량 버튼
export const BtnGroupQuantity = styled.div`
  display: flex;
  align-items: center;
  & button {
    width: 28px;
    height: 28px;
    padding: 7px 8px;
    background: var(--colWhite);
    border: 1px var(--colGray4) solid;
    cursor: pointer;
  }
  // 감소(minus) 버튼
  & button:first-child {
    border-right: none;
    border-radius: 4px 0 0 4px;
  }
  // 증가(plus) 버튼
  & button:last-child {
    border-left: none;
    border-radius: 0 4px 4px 0;
  }
  // 수량 숫자 입력
  & input {
    max-width: 40px;
    height: 28px;
    font-size: 14px;
    font-weight: 700;
    line-height: 16px;
    text-align: center;
    padding: 6px 0px;
    color: var(--colBlack);
    background: var(--colWhite);
    border: 1px var(--colGray4) solid;
    border-radius: 0;
    &:disabled {
      opacity: 1;
    }
    @media screen and (min-width: 768px) {
      font-size: 16px;
      font-weight: 700;
      line-height: 16px;
    }
  }
`

// 장바구니 결제버튼 영역
export const CartTotalPayment = styled.div`
  width: 100%;
  position: fixed;
  left: 0;
  bottom: 0;
  padding: 12px 20px;
  z-index: 1;
  border-top: 1px solid var(--colGray3);
  background: var(--colWhite);
  @media screen and (min-width: 768px) {
    position: static;
    margin: 20px 0 0 0;
    padding: 20px 0 0 0;
  }
`
